import { useState, useEffect } from "react";
import Tooltip from "../../components/common/Tooltip";
import LinkedBadge from "../common/LinkedBadge.jsx";
import { db } from "../../firebase/config";
import { client } from "../../hooks/Client";

/**
 * @param {object} props
 * @param {object} props.effect - OutputEffects row (needs ID, docid, Effect, Description, Standard)
 * @param {(e: object) => void} props.titleHandler - onChange for the title input
 * @param {(e: object) => void} props.descriptionHandler - onChange for the description textarea
 * @param {Record<string, string>} [props.portfolioNameById] - portfolio CompagnyID → display name (resolved once by the parent table; badge tooltip)
 */
const EditEffectTableData = ({ effect, titleHandler, descriptionHandler, portfolioNameById }) => {
  const [paired, setPaired] = useState(false);
  const [pairedParentId, setPairedParentId] = useState('');

  // Firestore lookup for “paired”. Only Status 'paired' rows count — same
  // definition as buildSyncedLinksMap, so the table badge and the navigator
  // badge can never disagree.
  const effectIsPaired = async (id) => {
    const snap = await db
      .collection('Synchronisations')
      .where('ProjectEffect', '==', id)
      // Tenant-scope + secure against the Members-based Synchronisations rule.
      // AI-CONTEXT: See docs/pitfalls.md#security-synchronisations-members-tenant-gate
      .where('Members', 'array-contains', client)
      .get();
    const pairedRows = snap.docs.map((doc) => doc.data()).filter((row) => row.Status === 'paired');
    setPaired(pairedRows.length > 0);
    setPairedParentId(pairedRows[0]?.Parent || '');
  };

  useEffect(() => {
    if (effect.ID) effectIsPaired(effect.ID);
  }, [effect.ID]);

  // Decide tooltip + whether we render <p> or <input>
  let tooltipContent = null;
  let isReadOnly = false;

  if (effect.Standard === 'true' && paired) {
    tooltipContent =
      'Dit gestandaardiseerde effect is gekoppeld aan een portfolio account en kan niet worden aangepast';
    isReadOnly = true;
  } else if (effect.Standard === 'true') {
    tooltipContent =
      'Gestandaardiseerde effecten kunnen niet worden aangepast';
    isReadOnly = true;
  } else if (paired) {
    tooltipContent =
      'Dit effect is gekoppeld aan een portfolio account en kan niet worden aangepast';
    isReadOnly = true;
  }

  return (
    <>
      <td>
        {isReadOnly ? (
          <Tooltip
            content={tooltipContent}
            width="80%"
            right="-220px"
            top={effect.Standard === 'true' ? '-35px' : '-35px'}
          >
            <p className="table-p">{effect.Effect}</p>
          </Tooltip>
        ) : (
          <Tooltip
            content={effect.Effect}
            width="80%"
            right="-220px"
            top="-35px"
          >
            <input
              type="text"
              data-docid={effect.docid}
              defaultValue={effect.Effect}
              placeholder="Effect"
              onChange={titleHandler}
            />
          </Tooltip>
        )}
        {paired && <LinkedBadge portfolioName={portfolioNameById?.[pairedParentId]} />}
      </td>
      <td className="common-table__cell--description">
        {isReadOnly ? (
          <Tooltip content={effect.Description || ''} width="80%" right="-220px" top="-35px">
            <p className="common-table__description">{effect.Description || '\u00A0'}</p>
          </Tooltip>
        ) : (
          <Tooltip content={effect.Description || ''} width="80%" right="-220px" top="-35px">
            <textarea
              className="common-table__description-textarea"
              data-docid={effect.docid}
              defaultValue={effect.Description || ''}
              placeholder="Beschrijving"
              onChange={descriptionHandler}
            />
          </Tooltip>
        )}
      </td>
    </>
  );
};

export default EditEffectTableData;
